import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Droplets, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WaterTechDashboard = ({ user, onLogout }) => {
  const [rooms, setRooms] = useState([]);
  const [todayStatus, setTodayStatus] = useState(null);
  const [roomData, setRoomData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const statusRes = await axios.get(
        `${API}/watering/today-status?scheduled_only=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const scheduledRooms = statusRes.data.rooms || [];
      setRooms(scheduledRooms);

      const wateredCount = scheduledRooms.filter(r => r.completed).length;
      setTodayStatus({
        ...statusRes.data,
        watered: wateredCount
      });

      const initialData = {};
      scheduledRooms.forEach(room => {
        // Prefer actual logged liters, then grower-scheduled liters, then 0
        const defaultLiters = room.actual_liters ?? room.scheduled_liters ?? 0;
        initialData[room.room_id] = {
          room_id: room.room_id,
          water_given_liters: defaultLiters,
          completed: room.completed || false
        };
      });
      setRoomData(initialData);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleWaterRoom = async (roomId) => {
    try {
      const token = localStorage.getItem('token');
      const room = rooms.find(r => r.room_id === roomId);
      await axios.post(`${API}/watering/log`, {
        room_id: roomId,
        water_given_liters: parseFloat(roomData[roomId].water_given_liters),
        notes: room?.scheduled_notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Watering logged successfully');
      setRoomData(prev => ({
        ...prev,
        [roomId]: { ...prev[roomId], completed: true }
      }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log watering');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F9FAF9]">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Water Management</h1>
              <p className="text-sm text-gray-500">Daily Room Watering</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user.full_name}</span>
              <Button onClick={onLogout} variant="outline" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {todayStatus && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle>Today's Watering Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                {todayStatus.watered === rooms.length && rooms.length > 0 ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                )}
                <div>
                  <p className="text-2xl font-bold">{todayStatus.watered}/{rooms.length}</p>
                  <p className="text-sm text-gray-600">Rooms Scheduled for Watering</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {rooms.length === 0 ? (
          <Card className="border-gray-200">
            <CardContent className="py-12 text-center text-gray-500">
              <Droplets className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">No Rooms Scheduled for Watering Today</p>
              <p className="text-sm">Grower will mark rooms that need watering each morning.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rooms.map((room) => (
            <Card key={room.room_id} className="border-gray-200" data-testid={`room-water-${room.room_number}`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-blue-600" />
                  {room.room_name}
                  {room.batch_name && (
                    <span className="text-sm font-normal text-gray-500">({room.batch_name})</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {room.scheduled_notes && (
                  <div className="p-3 rounded-md border border-amber-300 bg-amber-50" data-testid={`scheduled-notes-${room.room_number}`}>
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">
                      Grower's Note
                    </p>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">{room.scheduled_notes}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Water Amount (Liters)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={roomData[room.room_id]?.water_given_liters ?? room.scheduled_liters ?? 0}
                    onChange={(e) => setRoomData(prev => ({
                      ...prev,
                      [room.room_id]: { ...prev[room.room_id], water_given_liters: e.target.value }
                    }))}
                    disabled={roomData[room.room_id]?.completed}
                    data-testid={`water-input-${room.room_number}`}
                  />
                  {room.scheduled_liters != null && !roomData[room.room_id]?.completed && (
                    <p className="text-xs text-blue-700 mt-1">
                      Grower scheduled: {room.scheduled_liters}L
                    </p>
                  )}
                </div>
                
                {room.completed_at && (
                  <p className="text-xs text-gray-500">
                    Completed at: {new Date(room.completed_at).toLocaleString()}
                  </p>
                )}

                {roomData[room.room_id]?.completed || room.completed ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Completed ({room.actual_liters || roomData[room.room_id]?.water_given_liters}L)</span>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleWaterRoom(room.room_id)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid={`water-button-${room.room_number}`}
                  >
                    <Droplets className="w-4 h-4 mr-2" />
                    Mark as Watered
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </main>
    </div>
  );
};

export default WaterTechDashboard;
